import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarPlus, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Calendar as CalendarIcon,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { NumberInput } from '../../ui/number-input';
import { DateTimeInput } from '../../ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../../../lib/dateInputs';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import type {
  BreakTime, Category, DaySchedule, ExpectedTeamCount, GenerateParams, WOD,
} from '../../../types/heats';

export interface CourseWaveParams extends ExpectedTeamCount {
  wave_interval_minutes: number;
  participants_per_wave: number;
}

/**
 * « Combien d'équipes attends-tu ? » — le bloc qui permet de construire un
 * planning AVANT d'avoir des inscrits.
 *
 * Il vivait dans la seule branche CrossFit, alors que le besoin est le même
 * des deux côtés : c'est précisément ce qui empêchait un organisateur de
 * course de préparer ses vagues à l'avance.
 */
function ExpectedTeamCountCard<T extends ExpectedTeamCount>({
  params, onChange, teamCount, t,
}: {
  params: T;
  onChange: (p: T) => void;
  teamCount: number;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const custom = params.custom_team_count;
  const setMode = (mode: 'equal' | 'proportional') =>
    onChange({ ...params, team_distribution_mode: mode });

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <h3 className="text-sm font-bold text-foreground">{t('heats.generateDialog.teamCount')}</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={!custom}
              onCheckedChange={(checked) => onChange({
                ...params,
                custom_team_count: checked ? undefined : (teamCount || 50),
                team_distribution_mode: checked ? undefined : 'equal',
              })}
            />
            <Label className="text-sm">
              {t('heats.generateDialog.useRegisteredTeams', { count: teamCount })}
            </Label>
          </div>
          {custom !== undefined && (
            <>
              <span className="text-muted-foreground">|</span>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">
                  {t('heats.generateDialog.expectedTeams')}
                </Label>
                <NumberInput
                  value={custom}
                  onChange={(e) => onChange({
                    ...params,
                    custom_team_count: parseInt(e.target.value) || 50,
                  })}
                  className="w-20"
                  placeholder="50"
                />
              </div>
            </>
          )}
        </div>
        {custom !== undefined && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('heats.generateDialog.teamDistribution')}
            </Label>
            <div className="flex gap-4">
              {(['equal', 'proportional'] as const).map((mode) => {
                // Sans inscrit, « proportionnel » n'a aucune proportion à
                // reproduire : le serveur retombe sur l'équitable. On le dit
                // ici plutôt que de laisser un choix qui ne changerait rien.
                const inerte = mode === 'proportional' && teamCount === 0;
                return (
                  <div
                    key={mode}
                    className={`flex-1 p-3 rounded-lg border transition-colors ${
                      inerte
                        ? 'border-border opacity-50 cursor-not-allowed'
                        : params.team_distribution_mode === mode
                          ? 'border-white/40 bg-white/[0.08] cursor-pointer'
                          : 'border-border hover:border-muted-foreground cursor-pointer'
                    }`}
                    onClick={() => { if (!inerte) setMode(mode); }}
                  >
                    <div className="font-medium text-sm">
                      {t(`heats.generateDialog.distribution${mode === 'equal' ? 'Equal' : 'Proportional'}`)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {inerte
                        ? t('heats.generateDialog.distributionProportionalUnavailable')
                        : t(
                            `heats.generateDialog.distribution${mode === 'equal' ? 'Equal' : 'Proportional'}Desc`,
                            { count: teamCount },
                          )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Minutes saisies, ou le défaut si le champ ne contient pas un entier.
 *  `Number.isNaN` et non `||` : 0 est une valeur, pas une absence. */
const minutesOuDefaut = (raw: string, fallback: number): number => {
  const n = parseInt(raw, 10);
  return Number.isNaN(n) || n < 0 ? fallback : n;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bascule tout le formulaire entre « vagues » (course) et « heats » (CrossFit). */
  isCourseEvent: boolean;

  // Mode course
  courseWaveParams: CourseWaveParams;
  onCourseWaveParamsChange: (p: CourseWaveParams) => void;
  selectedCourse: string;
  onSelectedCourseChange: (v: string) => void;
  availableCourses: { id: string; name: string }[];
  categoryOrder: string[];
  onMoveCategoryOrder: (index: number, dir: -1 | 1) => void;
  /** Catégories à générer. Liste VIDE = toutes (et non « aucune »). */
  selectedCategories: string[];
  onToggleCategory: (categoryId: string) => void;

  // Mode CrossFit
  generateParams: GenerateParams;
  onGenerateParamsChange: (p: GenerateParams) => void;
  teamCount: number;

  // Commun
  categories: Category[];
  wods: WOD[];
  days: DaySchedule[];
  onUpdateDay: (index: number, updates: Partial<DaySchedule>) => void;
  onAddDay: () => void;
  onRemoveDay: (index: number) => void;
  onAddBreak: (dayIndex: number) => void;
  onUpdateBreak: (dayIndex: number, breakId: string, updates: Partial<BreakTime>) => void;
  onRemoveBreak: (dayIndex: number, breakId: string) => void;

  loading: boolean;
  onGenerate: () => void;
}

/**
 * Génération automatique du planning.
 *
 * Deux formulaires en un, selon le type d'événement : vagues (course) ou heats
 * par WOD (CrossFit). La partie basse — planification par jour, avec pauses —
 * est commune aux deux.
 */
const GenerateHeatsDialog: React.FC<Props> = ({
  open, onOpenChange, isCourseEvent,
  courseWaveParams, onCourseWaveParamsChange,
  selectedCourse, onSelectedCourseChange, availableCourses,
  categoryOrder, onMoveCategoryOrder, selectedCategories, onToggleCategory,
  generateParams, onGenerateParamsChange, teamCount,
  categories, wods, days,
  onUpdateDay, onAddDay, onRemoveDay, onAddBreak, onUpdateBreak, onRemoveBreak,
  loading, onGenerate,
}) => {
  const { t } = useTranslation();


  /* Catégories à générer et leur ordre — le même bloc pour les vagues (course)
     et les heats (CrossFit, 2026-09-27). Par défaut toutes, dans l'ordre
     d'affichage ; un groupe de catégories suit sa première catégorie. */
  const blocCategories = categoryOrder.length > 0 && (
    <>
                {categoryOrder.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t('heats.generateDialog.categoriesToGenerate')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('heats.generateDialog.categoriesToGenerateHint')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {categoryOrder.map(catId => {
                        const cat = categories.find(c => c.id === catId);
                        if (!cat) return null;
                        // Aucune sélection = tout est actif : on le MONTRE ainsi,
                        // sinon l'organisateur croit ne rien avoir choisi.
                        const active = selectedCategories.length === 0 || selectedCategories.includes(catId);
                        return (
                          <Badge
                            key={catId}
                            variant={active ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => onToggleCategory(catId)}
                          >
                            {cat.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ordre des catégories (vagues générées dans cet ordre) */}
                {categoryOrder.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t('heats.generateDialog.categoryOrder')}</Label>
                    <p className="text-xs text-muted-foreground">{t('heats.generateDialog.categoryOrderHint')}</p>
                    <div className="rounded-md border border-border divide-y divide-border">
                      {categoryOrder.map((catId, idx) => {
                        const cat = categories.find(c => c.id === catId);
                        if (!cat) return null;
                        return (
                          <div key={catId} className="flex items-center justify-between px-3 py-1.5 text-sm">
                            <span className="flex items-center gap-2">
                              <span className="text-muted-foreground font-mono w-5">{idx + 1}.</span>
                              {cat.name}
                            </span>
                            <span className="flex gap-1">
                              <Button
                                variant="ghost" size="sm" className="h-6 w-6 p-0"
                                disabled={idx === 0}
                                onClick={() => onMoveCategoryOrder(idx, -1)}
                              >
                                <ArrowUp size={14} />
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="h-6 w-6 p-0"
                                disabled={idx === categoryOrder.length - 1}
                                onClick={() => onMoveCategoryOrder(idx, 1)}
                              >
                                <ArrowDown size={14} />
                              </Button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isCourseEvent ? t('heats.generateDialog.titleWaves') : t('heats.generateDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isCourseEvent ? (
            /* Course Mode */
            <>
            <ExpectedTeamCountCard
              params={courseWaveParams}
              onChange={onCourseWaveParamsChange}
              teamCount={teamCount}
              t={t}
            />
            <Card>
              <CardContent className="pt-6 space-y-4">
                <h3 className="text-sm font-bold text-foreground">{t('heats.generateDialog.waveParams')}</h3>
                <Alert variant="info">
                  <AlertDescription>{t('heats.generateDialog.waveInfo')}</AlertDescription>
                </Alert>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('heats.generateDialog.waveInterval')}</Label>
                    <NumberInput min={1}
                      value={courseWaveParams.wave_interval_minutes}
                      onChange={(e) => onCourseWaveParamsChange({
                        ...courseWaveParams,
                        wave_interval_minutes: parseInt(e.target.value) || 5,
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('heats.generateDialog.participantsPerWave')}</Label>
                    <NumberInput min={1}
                      value={courseWaveParams.participants_per_wave}
                      onChange={(e) => onCourseWaveParamsChange({
                        ...courseWaveParams,
                        participants_per_wave: parseInt(e.target.value) || 50,
                      })}
                    />
                    {/* Le champ compte des équipes, pas des athlètes : sur un
                        événement en doubles, l'écart est du simple au double. */}
                    <p className="text-xs text-muted-foreground">
                      {t('heats.generateDialog.participantsPerWaveHint')}
                    </p>
                  </div>
                </div>

                {/* Sélection Course optionnelle */}
                <div className="space-y-2">
                  <Label>{t('heats.generateDialog.associatedCourseRequired')}</Label>
                  <Select
                    value={selectedCourse}
                    onValueChange={onSelectedCourseChange}
                    disabled={availableCourses.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={availableCourses.length === 0
                        ? t('heats.generateDialog.noCourseAvailable')
                        : t('heats.generateDialog.selectCourse')} />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Plus d'option « Aucune » : le serveur exige une course
                          sur une vague, la proposer serait proposer un refus. */}
                      {availableCourses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {availableCourses.length === 0
                      ? t('heats.generateDialog.courseHintEmpty')
                      : t('heats.generateDialog.courseHintSelect')}
                  </p>
                </div>

                {blocCategories}
              </CardContent>
            </Card>
            </>
          ) : (
            <>
              {/* CrossFit Mode */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <h3 className="text-sm font-bold text-foreground">{t('heats.generateDialog.generalParams')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('heats.generateDialog.availableLanes')}</Label>
                      <NumberInput
                        value={generateParams.lanes_available}
                        onChange={(e) => onGenerateParamsChange({
                          ...generateParams,
                          lanes_available: parseInt(e.target.value) || 10,
                        })}
                      />
                    </div>
                    {/* Une pause peut valoir 0 : `parseInt(x) || 5` renvoyait 5
                        dès qu'on tapait 0, et 0 était donc impossible à saisir. */}
                    <div className="space-y-2">
                      <Label>{t('heats.generateDialog.heatGap')}</Label>
                      <NumberInput
                        min={0}
                        value={generateParams.heat_gap}
                        onChange={(e) => onGenerateParamsChange({
                          ...generateParams,
                          heat_gap: minutesOuDefaut(e.target.value, 5),
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('heats.generateDialog.categoryGap')}</Label>
                      <NumberInput
                        min={0}
                        value={generateParams.category_gap}
                        onChange={(e) => onGenerateParamsChange({
                          ...generateParams,
                          category_gap: minutesOuDefaut(e.target.value, 5),
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('heats.generateDialog.categoryGapHint')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('heats.generateDialog.wodGap')}</Label>
                      <NumberInput
                        min={0}
                        value={generateParams.wod_gap}
                        onChange={(e) => onGenerateParamsChange({
                          ...generateParams,
                          wod_gap: minutesOuDefaut(e.target.value, 10),
                        })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {blocCategories && (
                <Card><CardContent className="pt-6 space-y-4">{blocCategories}</CardContent></Card>
              )}

              <ExpectedTeamCountCard
                params={generateParams}
                onChange={onGenerateParamsChange}
                teamCount={teamCount}
                t={t}
              />

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <h3 className="text-sm font-bold text-foreground">{t('heats.generateDialog.wodDuration')}</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={generateParams.use_wod_time_caps}
                        onCheckedChange={(checked) => onGenerateParamsChange({
                          ...generateParams,
                          use_wod_time_caps: checked,
                        })}
                      />
                      <Label className="text-sm">{t('heats.generateDialog.useWodTimeCaps')}</Label>
                    </div>
                    {!generateParams.use_wod_time_caps && (
                      <>
                        <span className="text-muted-foreground">|</span>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">
                            {t('heats.generateDialog.fixedDuration')}
                          </Label>
                          <NumberInput
                            value={generateParams.wod_duration}
                            onChange={(e) => onGenerateParamsChange({
                              ...generateParams,
                              wod_duration: parseInt(e.target.value) || 20,
                            })}
                            className="w-20"
                            placeholder="20"
                          />
                          <span className="text-sm text-muted-foreground">{t('heats.generateDialog.min')}</span>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Multi-Day Schedule */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-1">{t('heats.generateDialog.dayPlanning')}</h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('heats.generateDialog.dayFromEvent')}
            </p>
            <Accordion type="multiple" className="w-full">
              {days.map((day, index) => (
                <AccordionItem key={index} value={`day-${index}`}>
                  <AccordionTrigger className="text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarIcon size={16} className="text-muted-foreground" />
                      {t('heats.generateDialog.day', { number: index + 1 })} — {new Date(day.date).toLocaleDateString('fr-FR', {
                        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
                      })}
                      {!isCourseEvent && day.wod_ids.length > 0 && (
                        <Badge variant="default" className="ml-2">{day.wod_ids.length} WOD(s)</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                          <Label>{t('heats.generateDialog.dateLabel')}</Label>
                          <DateTimeInput type="date" value={day.date}
                            onChange={(e) => onUpdateDay(index, { date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('heats.generateDialog.startLabel')}</Label>
                          <DateTimeInput type="time" value={day.start_time}
                            onChange={(e) => onUpdateDay(index, { start_time: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('heats.generateDialog.endLabel')}</Label>
                          <DateTimeInput type="time" value={day.end_time}
                            onChange={(e) => onUpdateDay(index, { end_time: e.target.value })} />
                        </div>
                      </div>

                      {!isCourseEvent && (
                        <div className="space-y-2">
                          <Label>{t('heats.generateDialog.assignedWods')}</Label>
                          <div className="flex flex-wrap gap-2">
                            {wods.map(wod => (
                              <Badge
                                key={wod.id}
                                variant={day.wod_ids.includes(wod.id) ? 'default' : 'outline'}
                                className="cursor-pointer"
                                onClick={() => {
                                  const newWodIds = day.wod_ids.includes(wod.id)
                                    ? day.wod_ids.filter(id => id !== wod.id)
                                    : [...day.wod_ids, wod.id];
                                  onUpdateDay(index, { wod_ids: newWodIds });
                                }}
                              >
                                {wod.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Breaks */}
                      {day.breaks && day.breaks.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            {t('heats.generateDialog.scheduledBreaks')}
                          </Label>
                          {day.breaks.map(breakTime => (
                            <div key={breakTime.id} className="flex gap-2 items-center border rounded-md p-2">
                              <Input
                                value={breakTime.name}
                                onChange={(e) => onUpdateBreak(index, breakTime.id, { name: e.target.value })}
                                placeholder={t('heats.generateDialog.breakPlaceholder')}
                                className="flex-1"
                              />
                              <DateTimeInput
                                type="time" className="w-24"
                                value={breakTime.start_time}
                                onChange={(e) => onUpdateBreak(index, breakTime.id, { start_time: e.target.value })}
                              />
                              <DateTimeInput
                                type="time" className="w-24"
                                value={breakTime.end_time}
                                onChange={(e) => onUpdateBreak(index, breakTime.id, { end_time: e.target.value })}
                              />
                              <Button
                                variant="ghost" size="sm" className="text-destructive"
                                onClick={() => onRemoveBreak(index, breakTime.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => onAddBreak(index)}>
                          <Plus size={14} className="mr-1" /> {t('heats.generateDialog.addBreak')}
                        </Button>
                        {days.length > 1 && (
                          <Button variant="outline" size="sm" onClick={() => onRemoveDay(index)} className="text-destructive">
                            <Trash2 size={14} className="mr-1" /> {t('heats.generateDialog.removeDay')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Button variant="outline" size="sm" onClick={onAddDay} className="mt-2">
              <Plus size={14} className="mr-1" /> {t('heats.generateDialog.addDay')}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('heats.actions.cancel')}
          </Button>
          <Button onClick={() => { if (refuseIfDateInputIncomplete(t)) return; onGenerate(); }} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarPlus size={16} className="mr-2" />}
            {t('heats.actions.generate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateHeatsDialog;
